<template>
  <div
    class="asset-details-container"
    v-bind="$attrs"
  >
    <div
      v-if="loading"
      class="text-center py-5"
    >
      <div
        class="spinner-border text-primary"
        role="status"
      >
        <span class="visually-hidden">{{ $t('components.assetDetails.loading') }}</span>
      </div>
    </div>

    <div
      v-else-if="error"
      class="alert alert-danger"
    >
      <i class="bi bi-exclamation-triangle" />
      {{ error }}
    </div>

    <div
      v-else-if="asset"
      class="asset-details-wrapper"
    >
      <!-- Hero Header -->
      <div class="asset-hero">
        <div class="hero-content">
          <div class="hero-left">
            <div class="asset-icon-wrapper">
              <i class="bi bi-hdd-rack-fill" />
            </div>
            <div class="hero-info">
              <h1 class="asset-title">
                {{ asset.name || $t('components.assetDetails.unknownAsset') }}
              </h1>
              <div class="asset-meta">
                <span class="meta-item">
                  <i class="bi bi-upc-scan" />
                  {{ getSerialNumber() }}
                </span>
                <span
                  class="meta-item"
                  v-if="asset.company"
                >
                  <i class="bi bi-building" />
                  {{ asset.company.name }}
                </span>
              </div>
            </div>
          </div>
          <div class="hero-right">
            <div
              class="status-badge-large"
              :class="getOnlineStatusClass()"
            >
              <div class="status-pulse" />
              <span class="status-text">{{ getOnlineStatus() }}</span>
            </div>
            <button
              class="btn-back"
              @click="goBack"
            >
              <i class="bi bi-arrow-left" />
              {{ $t('components.assetDetails.backToList') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Modern Tabs -->
      <div class="modern-tabs">
        <button
          :class="{active: tabContent === TAB_DETAILS }"
          class="tab-btn"
          @click="setTab(TAB_DETAILS)"
        >
          <i class="bi bi-info-circle-fill" />
          <span>{{ $t('components.assetDetails.tabs.details') }}</span>
        </button>
        <button
          :class="{active: tabContent === TAB_HISTORY }"
          class="tab-btn"
          @click="setTab(TAB_HISTORY)"
        >
          <i class="bi bi-clock-history" />
          <span>{{ $t('components.assetDetails.tabs.history') }}</span>
        </button>
        <button
          v-if="isDknLmsAsset"
          :class="{active: tabContent === TAB_ALARMS }"
          class="tab-btn"
          @click="setTab(TAB_ALARMS)"
        >
          <i class="bi bi-bell-fill" />
          <span>{{ $t('components.assetDetails.alarms.tabLabel') }}</span>
        </button>
      </div>

      <!-- Tab Content -->
      <div class="tab-content-wrapper">
        <div
          v-if="tabContent === TAB_DETAILS"
          class="asset-details-content"
        >
          <div class="asset-overview-grid">
            <div class="asset-overview-chart">
              <!-- WAGO: oddzielny wykres per kategoria -->
              <template v-if="isWagoModbusAsset">
                <div
                  v-if="chartLoading"
                  class="chart-container chart-container-empty"
                  :class="{ 'dark-mode': isDarkMode }"
                >
                  <div class="chart-empty-state">
                    <div
                      class="spinner-border text-primary"
                      role="status"
                    >
                      <span class="visually-hidden">{{ $t('components.assetDetails.loading') }}</span>
                    </div>
                  </div>
                </div>
                <template v-else>
                  <WagoEnergyStats
                    v-if="energyStats"
                    :stats="energyStats"
                    :is-dark-mode="isDarkMode"
                  />
                  <WagoEnergyHeatmap
                    v-if="wagoHasAnyData"
                    :chart-series="heatmapSeries"
                    :is-dark-mode="isDarkMode"
                    :range-start-ts="heatmapRangeStartTs"
                    :range-end-ts="heatmapRangeEndTs"
                  />
                  <template
                    v-for="group in wagoChartGroups"
                    :key="group.id"
                  >
                    <div
                      v-if="wagoGroupHasData(group)"
                      class="chart-container mb-3"
                      :class="{ 'dark-mode': isDarkMode }"
                    >
                      <div class="chart-header">
                        <h3 class="chart-title">
                          <i class="bi bi-graph-up" />
                          <span>{{ $t(group.titleKey) }}</span>
                        </h3>
                        <div class="chart-actions">
                          <button
                            class="btn btn-custom"
                            @click="resetZoom('wago-chart-' + group.id)"
                            :title="$t('components.assetDetails.actions.resetZoom')"
                          >
                            <i class="bi bi-arrow-clockwise" />
                          </button>
                          <button
                            class="btn btn-custom"
                            @click="exportWagoGroupToPng(group)"
                            :title="$t('components.assetDetails.actions.exportPng')"
                          >
                            <i class="bi bi-filetype-png" />
                          </button>
                          <button
                            class="btn btn-custom"
                            @click="exportWagoGroupToJpg(group)"
                            :title="$t('components.assetDetails.actions.exportJpg')"
                          >
                            <i class="bi bi-filetype-jpg" />
                          </button>
                          <button
                            class="btn btn-custom"
                            @click="exportWagoGroupCsv(group)"
                            :title="$t('components.assetDetails.actions.exportCsv')"
                          >
                            <i class="bi bi-filetype-csv" />
                          </button>
                        </div>
                      </div>
                      <div class="chart-body">
                        <LineChart
                          :key="group.id + '-' + (isDarkMode ? 'dark' : 'light')"
                          :height="wagoChartHeight"
                          :chart-id="'wago-chart-' + group.id"
                          :chart-data="wagoChartData(group)"
                          :options="wagoChartOptions(group)"
                          :plugins="chartPlugins"
                        />
                      </div>
                    </div>
                  </template>
                  <div
                    v-if="!wagoHasAnyData"
                    class="chart-container chart-container-empty"
                    :class="{ 'dark-mode': isDarkMode }"
                  >
                    <div class="chart-empty-state">
                      <i class="bi bi-graph-up" />
                      <div class="chart-empty-text">
                        {{ chartError || $t('components.assetDetails.noHistory') }}
                      </div>
                    </div>
                  </div>
                </template>
              </template>
              <template v-else-if="isDknLmsAsset">
                <div
                  v-if="chartLoading"
                  class="chart-container chart-container-empty"
                  :class="{ 'dark-mode': isDarkMode }"
                >
                  <div class="chart-empty-state">
                    <div
                      class="spinner-border text-primary"
                      role="status"
                    >
                      <span class="visually-hidden">{{ $t('components.assetDetails.loading') }}</span>
                    </div>
                  </div>
                </div>
                <template v-else>
                  <template
                    v-for="group in dknChartGroups"
                    :key="group.id"
                  >
                    <div
                      v-if="dknGroupHasData(group)"
                      class="chart-container mb-3"
                      :class="{ 'dark-mode': isDarkMode }"
                    >
                      <div class="chart-header">
                        <h3 class="chart-title">
                          <i class="bi bi-graph-up" />
                          <span>{{ $t(group.titleKey) }}</span>
                        </h3>
                        <div class="chart-actions">
                          <button
                            class="btn btn-custom"
                            @click="resetZoom('dkn-chart-' + group.id)"
                            :title="$t('components.assetDetails.actions.resetZoom')"
                          >
                            <i class="bi bi-arrow-clockwise" />
                          </button>
                          <button
                            class="btn btn-custom"
                            @click="exportDknGroupToPng(group)"
                            :title="$t('components.assetDetails.actions.exportPng')"
                          >
                            <i class="bi bi-filetype-png" />
                          </button>
                          <button
                            class="btn btn-custom"
                            @click="exportDknGroupToJpg(group)"
                            :title="$t('components.assetDetails.actions.exportJpg')"
                          >
                            <i class="bi bi-filetype-jpg" />
                          </button>
                          <button
                            class="btn btn-custom"
                            @click="exportDknGroupCsv(group)"
                            :title="$t('components.assetDetails.actions.exportCsv')"
                          >
                            <i class="bi bi-filetype-csv" />
                          </button>
                        </div>
                      </div>
                      <div class="chart-body">
                        <LineChart
                          :key="'dkn-' + group.id + '-' + (isDarkMode ? 'dark' : 'light')"
                          :height="wagoChartHeight"
                          :chart-id="'dkn-chart-' + group.id"
                          :chart-data="dknChartData(group)"
                          :options="dknChartOptions(group)"
                          :plugins="chartPlugins"
                        />
                      </div>
                    </div>
                  </template>
                  <div
                    v-if="!dknHasAnyData"
                    class="chart-container chart-container-empty"
                    :class="{ 'dark-mode': isDarkMode }"
                  >
                    <div class="chart-empty-state">
                      <i class="bi bi-graph-up" />
                      <div class="chart-empty-text">
                        {{ chartError || $t('components.assetDetails.noHistory') }}
                      </div>
                    </div>
                  </div>
                </template>
              </template>
              <!-- DKN MPS: wykresy per grupa -->
              <template v-else-if="isDknMpsAsset">
                <div
                  v-if="chartLoading"
                  class="chart-container chart-container-empty"
                  :class="{ 'dark-mode': isDarkMode }"
                >
                  <div class="chart-empty-state">
                    <div
                      class="spinner-border text-primary"
                      role="status"
                    >
                      <span class="visually-hidden">{{ $t('components.assetDetails.loading') }}</span>
                    </div>
                  </div>
                </div>
                <template v-else>
                  <template
                    v-for="group in mpsChartGroups"
                    :key="group.id"
                  >
                    <div
                      v-if="dknGroupHasData(group)"
                      class="chart-container mb-3"
                      :class="{ 'dark-mode': isDarkMode }"
                    >
                      <div class="chart-header">
                        <h3 class="chart-title">
                          <i class="bi bi-graph-up" />
                          <span>{{ $t(group.titleKey) }}</span>
                        </h3>
                        <div class="chart-actions">
                          <button
                            class="btn btn-custom"
                            @click="resetZoom('mps-chart-' + group.id)"
                            :title="$t('components.assetDetails.actions.resetZoom')"
                          >
                            <i class="bi bi-arrow-clockwise" />
                          </button>
                          <button
                            class="btn btn-custom"
                            @click="exportMpsGroupToPng(group)"
                            :title="$t('components.assetDetails.actions.exportPng')"
                          >
                            <i class="bi bi-filetype-png" />
                          </button>
                          <button
                            class="btn btn-custom"
                            @click="exportMpsGroupToJpg(group)"
                            :title="$t('components.assetDetails.actions.exportJpg')"
                          >
                            <i class="bi bi-filetype-jpg" />
                          </button>
                          <button
                            class="btn btn-custom"
                            @click="exportMpsGroupCsv(group)"
                            :title="$t('components.assetDetails.actions.exportCsv')"
                          >
                            <i class="bi bi-filetype-csv" />
                          </button>
                        </div>
                      </div>
                      <div class="chart-body">
                        <LineChart
                          :key="'mps-' + group.id + '-' + (isDarkMode ? 'dark' : 'light')"
                          :height="wagoChartHeight"
                          :chart-id="'mps-chart-' + group.id"
                          :chart-data="dknChartData(group)"
                          :options="dknChartOptions(group)"
                          :plugins="chartPlugins"
                        />
                      </div>
                    </div>
                  </template>
                  <div
                    v-if="!mpsHasAnyData"
                    class="chart-container chart-container-empty"
                    :class="{ 'dark-mode': isDarkMode }"
                  >
                    <div class="chart-empty-state">
                      <i class="bi bi-graph-up" />
                      <div class="chart-empty-text">
                        {{ chartError || $t('components.assetDetails.noHistory') }}
                      </div>
                    </div>
                  </div>
                </template>
              </template>
              <!-- DKN UART (Daikin Mini Zeas): wykresy per grupa -->
              <template v-else-if="isDknUartAsset">
                <div
                  v-if="chartLoading"
                  class="chart-container chart-container-empty"
                  :class="{ 'dark-mode': isDarkMode }"
                >
                  <div class="chart-empty-state">
                    <div
                      class="spinner-border text-primary"
                      role="status"
                    >
                      <span class="visually-hidden">{{ $t('components.assetDetails.loading') }}</span>
                    </div>
                  </div>
                </div>
                <template v-else>
                  <template
                    v-for="group in uartChartGroups"
                    :key="group.id"
                  >
                    <div
                      v-if="dknGroupHasData(group)"
                      class="chart-container mb-3"
                      :class="{ 'dark-mode': isDarkMode }"
                    >
                      <div class="chart-header">
                        <h3 class="chart-title">
                          <i class="bi bi-graph-up" />
                          <span>{{ $t(group.titleKey) }}</span>
                        </h3>
                        <div class="chart-actions">
                          <button
                            class="btn btn-custom"
                            @click="resetZoom('uart-chart-' + group.id)"
                            :title="$t('components.assetDetails.actions.resetZoom')"
                          >
                            <i class="bi bi-arrow-clockwise" />
                          </button>
                          <button
                            class="btn btn-custom"
                            @click="exportUartGroupToPng(group)"
                            :title="$t('components.assetDetails.actions.exportPng')"
                          >
                            <i class="bi bi-filetype-png" />
                          </button>
                          <button
                            class="btn btn-custom"
                            @click="exportUartGroupToJpg(group)"
                            :title="$t('components.assetDetails.actions.exportJpg')"
                          >
                            <i class="bi bi-filetype-jpg" />
                          </button>
                          <button
                            class="btn btn-custom"
                            @click="exportUartGroupCsv(group)"
                            :title="$t('components.assetDetails.actions.exportCsv')"
                          >
                            <i class="bi bi-filetype-csv" />
                          </button>
                        </div>
                      </div>
                      <div class="chart-body">
                        <LineChart
                          :key="'uart-' + group.id + '-' + (isDarkMode ? 'dark' : 'light')"
                          :height="wagoChartHeight"
                          :chart-id="'uart-chart-' + group.id"
                          :chart-data="dknChartData(group)"
                          :options="dknChartOptions(group)"
                          :plugins="chartPlugins"
                        />
                      </div>
                    </div>
                  </template>
                  <div
                    v-if="!uartHasAnyData"
                    class="chart-container chart-container-empty"
                    :class="{ 'dark-mode': isDarkMode }"
                  >
                    <div class="chart-empty-state">
                      <i class="bi bi-graph-up" />
                      <div class="chart-empty-text">
                        {{ chartError || $t('components.assetDetails.noHistory') }}
                      </div>
                    </div>
                  </div>
                </template>
              </template>
              <!-- Non-WAGO: oryginalny pojedynczy wykres -->
              <template v-else-if="!isChartFullscreen">
                <div
                  v-if="chartHasData"
                  class="chart-container"
                  :class="{ 'dark-mode': isDarkMode }"
                >
                  <div class="chart-header">
                    <h3 class="chart-title">
                      <i class="bi bi-graph-up" />
                      <span>{{ getChartTitle() }}</span>
                    </h3>
                    <div class="chart-actions">
                      <button
                        class="btn btn-custom"
                        :disabled="!chartHasData"
                        @click="openChartFullscreen"
                        :title="$t('components.assetDetails.actions.fullscreen')"
                      >
                        <i class="bi bi-arrows-fullscreen" />
                      </button>
                      <button
                        class="btn btn-custom"
                        :disabled="!chartHasData"
                        @click="resetZoom('asset-chart')"
                        :title="$t('components.assetDetails.actions.resetZoom')"
                      >
                        <i class="bi bi-arrow-clockwise" />
                      </button>
                      <button
                        class="btn btn-custom chart-action-export"
                        :disabled="!chartHasData"
                        @click="exportToPng()"
                        :title="$t('components.assetDetails.actions.exportPng')"
                      >
                        <i class="bi bi-filetype-png" />
                      </button>
                      <button
                        class="btn btn-custom chart-action-export"
                        :disabled="!chartHasData"
                        @click="exportToJpg()"
                        :title="$t('components.assetDetails.actions.exportJpg')"
                      >
                        <i class="bi bi-filetype-jpg" />
                      </button>
                      <button
                        class="btn btn-custom chart-action-export"
                        :disabled="!chartHasData"
                        @click="exportCsv()"
                        :title="$t('components.assetDetails.actions.exportCsv')"
                      >
                        <i class="bi bi-filetype-csv" />
                      </button>
                    </div>
                  </div>
                  <div class="chart-body">
                    <LineChart
                      :key="chartRenderKey"
                      :height="chartHeight"
                      chart-id="asset-chart"
                      :chart-data="chartData"
                      :options="chartOptions"
                      :plugins="chartPlugins"
                    />
                  </div>
                </div>
                <div
                  v-else
                  class="chart-container chart-container-empty"
                  :class="{ 'dark-mode': isDarkMode }"
                >
                  <div
                    v-if="chartLoading"
                    class="chart-empty-state"
                  >
                    <div
                      class="spinner-border text-primary"
                      role="status"
                    >
                      <span class="visually-hidden">{{ $t('components.assetDetails.loading') }}</span>
                    </div>
                  </div>
                  <div
                    v-else
                    class="chart-empty-state"
                  >
                    <i class="bi bi-graph-up" />
                    <div class="chart-empty-text">
                      {{ chartError || $t('components.assetDetails.noHistory') }}
                    </div>
                  </div>
                </div>
              </template>
            </div>

            <div class="asset-overview-side">
              <div class="asset-tile asset-tile-image">
                <div class="asset-tile-header">
                  <div class="asset-tile-title">
                    <i class="bi bi-image" />
                    <span>{{ $t('components.assetDetails.image.title') }}</span>
                  </div>
                  <button
                    class="asset-tile-action"
                    type="button"
                    :title="$t('components.assetDetails.image.open')"
                    @click="openImageModal"
                  >
                    <i class="bi bi-arrows-fullscreen" />
                  </button>
                </div>
                <div
                  class="asset-tile-media"
                  @click="openImageModal"
                >
                  <img
                    :src="getAssetImageOrPlaceholder()"
                    :alt="asset.name || $t('components.assetDetails.image.alt')"
                    class="asset-tile-media-img"
                  >
                  <div class="asset-tile-media-overlay">
                    <i class="bi bi-arrows-fullscreen" />
                    <span>{{ $t('components.assetDetails.image.clickToZoom') }}</span>
                  </div>
                </div>
              </div>

              <div class="asset-tile asset-tile-stats">
                <div class="asset-tile-header">
                  <div class="asset-tile-title">
                    <i class="bi bi-speedometer2" />
                    <span>{{ $t('components.assetDetails.currentParameters') }}</span>
                  </div>
                </div>
                <template v-if="isDevice">
                  <div class="asset-stats-grid">
                    <div class="asset-stat">
                      <div class="asset-stat-header">
                        <i class="bi bi-arrows-move" />
                        <span>{{ $t('components.assets.dataGrids.xAxis') }}</span>
                      </div>
                      <div class="asset-stat-value">
                        {{ getAxisValue('X_AXIS') }}
                      </div>
                    </div>
                    <div class="asset-stat">
                      <div class="asset-stat-header">
                        <i class="bi bi-arrows-vertical" />
                        <span>{{ $t('components.assets.dataGrids.yAxis') }}</span>
                      </div>
                      <div class="asset-stat-value">
                        {{ getAxisValue('Y_AXIS') }}
                      </div>
                    </div>
                    <div class="asset-stat">
                      <div class="asset-stat-header">
                        <i class="bi bi-arrows-collapse" />
                        <span>{{ $t('components.assets.dataGrids.zAxis') }}</span>
                      </div>
                      <div class="asset-stat-value">
                        {{ getAxisValue('Z_AXIS') }}
                      </div>
                    </div>
                    <div class="asset-stat">
                      <div class="asset-stat-header">
                        <i class="bi bi-activity" />
                        <span>{{ $t('components.assets.dataGrids.magnitude') }}</span>
                      </div>
                      <div class="asset-stat-value">
                        {{ getAxisMagnitude() }}
                      </div>
                    </div>
                    <div class="asset-stat">
                      <div class="asset-stat-header">
                        <i class="bi bi-activity" />
                        <span>{{ $t('components.assets.dataGrids.magnitudeNoGravity') }}</span>
                      </div>
                      <div class="asset-stat-value">
                        {{ getAxisMagnitudeNoGravity() }}
                      </div>
                    </div>
                  </div>
                </template>
                <template v-else-if="isDknUartAsset">
                  <DknUartLatestParamsPanel :latest-params="asset.latest_params || []" />
                </template>
                <template v-else-if="isDknMpsAsset">
                  <DknMpsLatestParamsPanel :latest-params="asset.latest_params || []" />
                </template>
                <template v-else-if="isDknLmsAsset">
                  <DknLmsLatestParamsPanel :latest-params="asset.latest_params || []" />
                </template>
                <template v-else-if="isWagoModbusAsset">
                  <WagoLatestParamsPanel
                    :latest-params="asset.latest_params || []"
                    :is-dark-mode="isDarkMode"
                  />
                </template>
                <template v-else-if="isModbusAsset">
                  <ModbusLatestParamsPanel :latest-params="asset.latest_params || []" />
                </template>
                <template v-else>
                  <div class="asset-stats-grid">
                    <div
                      class="asset-stat"
                      :style="{ '--stat-accent': getTemperatureColor() }"
                    >
                      <div class="asset-stat-header">
                        <i class="bi bi-thermometer-half" />
                        <span>{{ $t('components.assets.dataGrids.temperature') }}</span>
                      </div>
                      <div class="asset-stat-value">
                        {{ getTemperature() }}
                      </div>
                    </div>
                    <div
                      class="asset-stat"
                      :style="{ '--stat-accent': getHumidityColor() }"
                    >
                      <div class="asset-stat-header">
                        <i class="bi bi-droplet-half" />
                        <span>{{ $t('components.assets.dataGrids.humidity') }}</span>
                      </div>
                      <div class="asset-stat-value">
                        {{ getHumidity() }}
                      </div>
                    </div>
                    <div class="asset-stat">
                      <div class="asset-stat-header">
                        <i class="bi bi-lightning-charge-fill" />
                        <span>{{ $t('components.assets.dataGrids.power') }}</span>
                      </div>
                      <div class="asset-stat-value">
                        {{ getPowerSupply() }}
                      </div>
                    </div>
                    <div
                      class="asset-stat"
                      :class="getBatteryStatusClass()"
                    >
                      <div class="asset-stat-header">
                        <i class="bi bi-battery-charging" />
                        <span>{{ $t('components.assets.dataGrids.batteryStatus') }}</span>
                      </div>
                      <div class="asset-stat-value">
                        {{ getBatteryStatus() }}
                      </div>
                    </div>
                  </div>
                </template>
              </div>

              <div class="asset-tile asset-tile-info">
                <div class="asset-tile-header">
                  <div class="asset-tile-title">
                    <i class="bi bi-info-circle" />
                    <span>{{ $t('components.assetDetails.basicInfo') }}</span>
                  </div>
                </div>
                <ul class="asset-info-list">
                  <li class="asset-info-item">
                    <span class="asset-info-label">{{ $t('components.assets.dataGrids.serialNumber') }}</span>
                    <span class="asset-info-value">{{ getSerialNumber() }}</span>
                  </li>
                  <li
                    v-if="asset.company"
                    class="asset-info-item"
                  >
                    <span class="asset-info-label">{{ $t('components.assets.dataGrids.company') }}</span>
                    <span class="asset-info-value">{{ asset.company.name }}</span>
                  </li>
                  <li class="asset-info-item">
                    <span class="asset-info-label">{{ $t('components.assets.dataGrids.lastActivity') }}</span>
                    <span class="asset-info-value">{{ getLastActivity() }}</span>
                  </li>
                  <li
                    v-if="asset.location_description"
                    class="asset-info-item"
                  >
                    <span class="asset-info-label">{{ $t('components.assets.dataGrids.locationDescription') }}</span>
                    <span class="asset-info-value">{{ asset.location_description }}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        <div v-if="tabContent === TAB_HISTORY">
          <AssetHistory
            :asset-id="asset.id"
          />
        </div>

        <div v-if="tabContent === TAB_ALARMS">
          <DknAlarmTable
            :alarms="alarms"
            :loading="alarmsLoading"
            :error="alarmsError"
            :total-alarms="alarmsSummary?.total ?? 0"
            :is-dark-mode="isDarkMode"
          />
        </div>
      </div>
    </div>
  </div>

  <teleport to="body">
    <div
      v-if="isChartFullscreen"
      class="chart-fullscreen-overlay"
      :class="{ 'dark-mode': isDarkMode }"
      @click.self="closeChartFullscreen"
    >
      <div
        class="chart-container chart-container-fullscreen"
        :class="{ 'dark-mode': isDarkMode }"
      >
        <div class="chart-header">
          <h3 class="chart-title">
            <i class="bi bi-graph-up" />
            <span>{{ getChartTitle() }}</span>
          </h3>
          <div class="chart-actions">
            <button
              class="btn btn-custom"
              type="button"
              @click="closeChartFullscreen"
              :title="$t('components.assetDetails.actions.close')"
            >
              <i class="bi bi-x-lg" />
            </button>
            <button
              class="btn btn-custom"
              :disabled="!chartHasData"
              @click="resetZoom('asset-chart')"
              :title="$t('components.assetDetails.actions.resetZoom')"
            >
              <i class="bi bi-arrow-clockwise" />
            </button>
            <button
              class="btn btn-custom"
              :disabled="!chartHasData"
              @click="exportToPng()"
              :title="$t('components.assetDetails.actions.exportPng')"
            >
              <i class="bi bi-filetype-png" />
            </button>
            <button
              class="btn btn-custom"
              :disabled="!chartHasData"
              @click="exportToJpg()"
              :title="$t('components.assetDetails.actions.exportJpg')"
            >
              <i class="bi bi-filetype-jpg" />
            </button>
            <button
              class="btn btn-custom"
              :disabled="!chartHasData"
              @click="exportCsv()"
              :title="$t('components.assetDetails.actions.exportCsv')"
            >
              <i class="bi bi-filetype-csv" />
            </button>
          </div>
        </div>
        <div
          v-if="chartHasData"
          class="chart-body"
        >
          <LineChart
            :key="chartRenderKey"
            :height="fullscreenChartHeight"
            chart-id="asset-chart"
            :chart-data="chartData"
            :options="chartOptions"
            :plugins="chartPlugins"
          />
        </div>
        <div
          v-else
          class="chart-body chart-fullscreen-empty"
        >
          <div
            v-if="chartLoading"
            class="chart-empty-state"
          >
            <div
              class="spinner-border text-primary"
              role="status"
            >
              <span class="visually-hidden">{{ $t('components.assetDetails.loading') }}</span>
            </div>
          </div>
          <div
            v-else
            class="chart-empty-state"
          >
            <i class="bi bi-graph-up" />
            <div class="chart-empty-text">
              {{ chartError || $t('components.assetDetails.noHistory') }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { useI18n } from 'vue-i18n'
import { getHumanDateTime, getHumanDate, generateCsv } from '@/utils/tools'
import { getAssetDetails, getAssetHistoryRange, getAssetEnergyStats, getAssetAlarms } from '@/crud/asset/getters'
import { LineChart } from 'vue-chart-3'
import { Chart, ChartData, ChartOptions, registerables } from 'chart.js'
import 'chartjs-adapter-moment'
import zoomPlugin from 'chartjs-plugin-zoom'
import { resetZoom, exportToImg } from '@/utils/chart'
import AssetHistory from '@/components/content/AssetHistory.vue'
import WagoEnergyHeatmap from '@/components/content/WagoEnergyHeatmap.vue'
import WagoEnergyStats from '@/components/content/WagoEnergyStats.vue'
import ModbusLatestParamsPanel from '@/components/content/ModbusLatestParamsPanel.vue'
import WagoLatestParamsPanel from '@/components/content/WagoLatestParamsPanel.vue'
import DknLmsLatestParamsPanel from '@/components/content/DknLmsLatestParamsPanel.vue'
import DknMpsLatestParamsPanel from '@/components/content/DknMpsLatestParamsPanel.vue'
import DknUartLatestParamsPanel from '@/components/content/DknUartLatestParamsPanel.vue'
import DknAlarmTable from '@/components/content/DknAlarmTable.vue'
import {
  buildSeriesRecord,
  clipSeriesRecordByTimeRange,
  DKN_LMS_CHART_GROUPS,
  DKN_MPS_CHART_GROUPS,
  DKN_UART_CHART_GROUPS,
  SERIES_META,
  WAGO_CHART_GROUPS,
  type ChartSeriesRecord,
  type DknChartGroup,
  type SeriesKey,
  type WagoChartGroup
} from '@/utils/asset-history-series'

Chart.register(...registerables)
Chart.register(zoomPlugin)

const TAB_DETAILS = 0
const TAB_HISTORY = 1
const TAB_ALARMS  = 2

const LATEST_PARAM = {
  MAC_ADDRESS: 'TL_EYE_MAC_ADDRESS',
  TEMPERATURE: 'TL_EYE_TEMPERATURE',
  HUMIDITY: 'TL_EYE_HUMIDITY',
  POWER_SUPPLY: 'TL_EYE_POWER_SUPPLY',
  BATTERY_VOLTAGE: 'TL_EYE_BATTERY_VOLTAGE',
  LOW_BATTERY: 'TL_EYE_LOW_BATTERY',
  MAGNITUDE: 'MAGNITUDE',
  MAGNITUDE_NO_GRAVITY: 'MAGNITUDE_NO_GRAVITY'
} as const


const DKN_LMS_SERIES_KEYS = [
  'dknLmsSv',
  'dknLmsSc',
  'dknLmsDfs',
  'dknLmsAlr',
  'dknLmsVsr',
  'dknLmsSh',
  'dknLmsPpu',
  'dknLmsShb',
  'dknLmsPpub',
  'dknLmsTgs',
  'dknLmsTeu',
  'dknLmsTeub',
  'dknLmsTgsb',
  'dknLmsSdt',
  'dknLmsSdtb',
  'dknLmsScb',
  'dknLmsSt',
  'dknLmsAh',
  'dknLmsAl',
  'dknLmsR4',
  'dknLmsD2',
  'dknLmsDfm',
  'dknLmsDas',
  'dknLmsCht',
  'dknLmsFia',
  'dknLmsFoi',
  'dknLmsFot',
  'dknLmsGlo',
  'dknLmsHi',
  'dknLmsU1'
] as const

const DKN_MPS_SERIES_KEYS: readonly SeriesKey[] = DKN_MPS_CHART_GROUPS.flatMap((g) => [...g.keys])

export default defineComponent({
  name: 'AssetDetailsView',
  inheritAttrs: false,
  emits: ['pageName'],
  components: {
    AssetHistory,
    WagoEnergyHeatmap,
    WagoEnergyStats,
    ModbusLatestParamsPanel,
    WagoLatestParamsPanel,
    DknLmsLatestParamsPanel,
    DknMpsLatestParamsPanel,
    DknUartLatestParamsPanel,
    DknAlarmTable,
    LineChart
  },
  setup(props, context) {
    const i18n = useI18n()
    context.emit('pageName', i18n.t('components.assetDetails.title'))

    return { i18n }
  },
	  data() {
	    return {
	      TAB_DETAILS: TAB_DETAILS,
	      TAB_HISTORY: TAB_HISTORY,
        TAB_ALARMS: TAB_ALARMS,
	      tabContent: TAB_DETAILS,
	      asset: null as any,
	        isDevice: false,
	      chartSeries: buildSeriesRecord({}, (key) => key) as ChartSeriesRecord,
        heatmapSeries: buildSeriesRecord({}, (key) => key) as ChartSeriesRecord,
        heatmapRangeStartTs: null as number | null,
        heatmapRangeEndTs: null as number | null,
	      chartLoading: false,
	      chartError: null as string | null,
	      loading: true,
	      error: null as string | null,
	      isDarkMode: false,
	      isChartFullscreen: false,
	      fullscreenChartHeight: 720,
	      bodyOverflowBeforeFullscreen: '' as string,
	      chartHeight: 650,
	      wagoChartHeight: 320,
      dknSeriesVisibility: {} as Record<string, boolean>,
	      themeObserver: null as MutationObserver | null,
        energyStats: null as any,
        energyStatsLoading: false,
        alarms: [] as any[],
        alarmsSummary: null as any,
        alarmsLoading: false,
        alarmsError: null as string | null,
	    }
	  },
  mounted() {
    this.fetchAssetDetails()
    this.checkDarkMode()
    this.updateChartHeight()
    window.addEventListener('resize', this.updateChartHeight)

    // Listen for theme changes
    this.themeObserver = new MutationObserver(() => {
      this.checkDarkMode()
    })
    this.themeObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    })
  },
	  unmounted() {
	    // Clean up observer
	    if (this.themeObserver) {
	      this.themeObserver.disconnect()
	    }
	    this.closeChartFullscreen()
	    window.removeEventListener('resize', this.updateChartHeight)
	  },
	  methods: {
    setTab(tab: number): void {
      this.tabContent = tab
      if (tab === TAB_ALARMS && this.alarms.length === 0 && !this.alarmsLoading) {
        const assetId = Array.isArray(this.$route.params.id)
          ? this.$route.params.id[0]
          : this.$route.params.id
        if (assetId) {
          this.fetchAlarms(assetId)
        }
      }
    },
    getAssetImage() {
      if (!this.asset) return null
      // Check multiple possible field names
      return this.asset.imagePath ||
             this.asset.image_path ||
             this.asset.image ||
             this.asset.image_url ||
             this.asset.photo ||
             this.asset.photo_url ||
             this.asset.picture_url ||
             this.asset.picture ||
             this.asset.device_model?.image ||
             this.asset.device_model?.image_url ||
             null
    },
    getAssetImageOrPlaceholder() {
      const assetImage = this.getAssetImage()
      if (assetImage) {
        if (typeof assetImage === 'string' && assetImage.includes('var/uploads/')) {
          return this.convertPathToUrl(assetImage)
        }
        return assetImage
      }

      // If asset has no image, try to use location image
      const locationImage = this.asset?.asset_group?.location?.image_path
      if (locationImage) {
        return this.convertPathToUrl(locationImage)
      }

      // Fallback to placeholder
      return '/img/asset-placeholder.png'
    },
    convertPathToUrl(filePath: string): string {
      let relativePath = filePath
      if (filePath.includes('/public/var/uploads/')) {
        relativePath = filePath.substring(filePath.indexOf('/var/uploads/'))
      } else if (filePath.includes('var/uploads/')) {
        const index = filePath.indexOf('var/uploads/')
        relativePath = '/' + filePath.substring(index)
      }

      if (!relativePath.startsWith('/')) {
        relativePath = '/' + relativePath
      }

      const base = (process.env.VUE_APP_API_BASE_URL || '').replace(/\/+$/, '')
      return base ? `${base}${relativePath}` : relativePath
    },
    openImageModal() {
      const imageUrl = this.getAssetImageOrPlaceholder()
      if (imageUrl) {
        window.open(imageUrl, '_blank')
      }
    },
    async fetchAssetDetails() {
      const assetId = Array.isArray(this.$route.params.id)
        ? this.$route.params.id[0]
        : this.$route.params.id

      if (!assetId) {
        this.error = this.$t('components.assetDetails.noIdProvided')
        this.loading = false
        return
      }

      try {
        this.loading = true
        const source = typeof this.$route.query.source === 'string' ? this.$route.query.source : undefined
        const isDevice = source === 'device'
        const response = await getAssetDetails(assetId, isDevice ? 'device' : undefined)
        this.asset = response.data.asset || response.data
        this.isDevice = isDevice
        this.fetchChartSeries(assetId, isDevice ? 'device' : undefined)
        if (!isDevice && this.isWagoModbusAsset) {
          this.fetchEnergyStats(assetId)
        }
      } catch (err) {
        console.error('Error fetching asset details:', err)
        this.error = this.$t('components.assetDetails.errorLoading')
      } finally {
        this.loading = false
      }
    },
    goBack() {
      this.$router.push('/assets')
    },
    getExportSerialName(): string {
      return this.asset?.serial_number || (this.$t('components.assetDetails.exportFallbackName') as string)
    },
    findLatestParam(paramName: string): any | null {
      const params = this.asset?.latest_params
      if (!Array.isArray(params) || !params.length) return null
      return params.find((param: any) => param?.can_params_io?.param_name === paramName) || null
    },
    getLatestParamNumber(paramName: string): number | null {
      const raw = this.findLatestParam(paramName)?.value
      const numeric = typeof raw === 'number' ? raw : typeof raw === 'string' ? parseFloat(raw) : NaN
      return Number.isFinite(numeric) ? numeric : null
    },
    getAxisValue(paramName: string) {
      const value = this.getLatestParamNumber(paramName)
      if (value === null) return '—'
      return `${value} mG`
    },
    getAxisMagnitudeValue() {
      return this.getLatestParamNumber(LATEST_PARAM.MAGNITUDE)
    },
    getAxisMagnitude() {
      const magnitude = this.getAxisMagnitudeValue()
      if (magnitude === null) return '—'
      return `${magnitude.toFixed(2)} mG`
    },
    getAxisMagnitudeNoGravity() {
      const magnitudeNoGravity = this.getLatestParamNumber(LATEST_PARAM.MAGNITUDE_NO_GRAVITY)
      if (magnitudeNoGravity === null) return '—'
      return `${magnitudeNoGravity.toFixed(2)} mG`
    },
    stripUnitFromLabel(label: string): string {
      return label.replace(/\s*\\((kW|kVA|V)\\)\\s*$/i, '').trim()
    },
    getChartTitle() {
      if (this.isDevice) {
        return this.$t('components.assetDetails.chartTitleDevice')
      }
      if (this.isDknLmsAsset) {
        return this.$t('components.assetDetails.chartTitleDknLms')
      }
      return this.$t('components.assetDetails.chartTitle')
    },
    getSerialNumber() {
      if (!this.asset) return '—'
      const mac = this.findLatestParam(LATEST_PARAM.MAC_ADDRESS)?.value
      if (mac !== null && mac !== undefined && mac !== '') {
        return String(mac)
      }
      return this.asset.serial_number || '—'
    },
    getTemperature() {
      const value = this.getLatestParamNumber(LATEST_PARAM.TEMPERATURE)
      if (value === null) return '—'
      return `${value.toFixed(1)} °C`
    },
    getPowerSupply() {
      const milliVolts = this.getLatestParamNumber(LATEST_PARAM.POWER_SUPPLY) ??
        this.getLatestParamNumber(LATEST_PARAM.BATTERY_VOLTAGE)
      if (milliVolts === null) return '—'

      const volts = milliVolts / 1000
      return `${volts.toFixed(2)} V`
    },
    getHumidity() {
      const value = this.getLatestParamNumber(LATEST_PARAM.HUMIDITY)
      if (value === null) return '—'
      return `${value.toFixed(1)} %`
    },
    isBatteryOk(): boolean | null {
      const value = this.getLatestParamNumber(LATEST_PARAM.LOW_BATTERY)
      if (value === null) return null
      return Math.round(value) === 0
    },
    getBatteryStatusClass() {
      const ok = this.isBatteryOk()
      if (ok === null) return ''
      return ok ? 'status-ok' : 'status-bad'
    },
    getBatteryStatus() {
      const ok = this.isBatteryOk()
      if (ok === null) return '—'
      return ok ? this.$t('common.ok') : this.$t('common.bad')
    },
    isOnline(): boolean {
      const lastSeen = this.asset?.last_seen
      if (!lastSeen) return false
      const now = Math.floor(Date.now() / 1000)
      const diff = now - lastSeen
      return diff < 30 * 60
    },
    getOnlineStatus() {
      return this.isOnline() ? this.$t('common.online') : this.$t('common.offline')
    },
    getOnlineStatusClass() {
      return this.isOnline() ? 'online' : 'offline'
    },
    getTemperatureValue() {
      return this.getLatestParamNumber(LATEST_PARAM.TEMPERATURE) ?? 0
    },
    getHumidityValue() {
      return this.getLatestParamNumber(LATEST_PARAM.HUMIDITY) ?? 0
    },
    getLastActivity() {
      if (!this.asset || !this.asset.last_seen) return '—'
      return getHumanDateTime(this.asset.last_seen)
    },
    getTemperatureColor() {
      const temp = this.getTemperatureValue()
      if (temp < -10) return 'rgb(59, 130, 246)' // Blue
      if (temp >= -10 && temp < 20) return 'rgb(16, 185, 129)' // Green
      if (temp >= 20 && temp < 30) return 'rgb(251, 146, 60)' // Orange
      return 'rgb(220, 38, 38)' // Red
    },
    getHumidityColor() {
      const humidity = this.getHumidityValue()
      if (humidity < 39) return 'rgb(220, 38, 38)' // Red - Too Low
      if (humidity >= 39 && humidity <= 60) return 'rgb(16, 185, 129)' // Green - Optimal
      if (humidity > 60 && humidity <= 70) return '#252422' // Orange - Too High
      return 'rgb(124, 58, 237)' // Purple - Much Too High
    },
	    checkDarkMode() {
	      this.isDarkMode = document.body.classList.contains('dark-theme')
	    },
	    openChartFullscreen() {
	      if (this.isChartFullscreen) return
	      this.isChartFullscreen = true
	      this.bodyOverflowBeforeFullscreen = document.body.style.overflow
	      document.body.style.overflow = 'hidden'
	      this.updateFullscreenChartHeight()
	      window.addEventListener('resize', this.updateFullscreenChartHeight)
	      window.addEventListener('orientationchange', this.updateFullscreenChartHeight)
	      document.addEventListener('keydown', this.onFullscreenKeydown)
	    },
	    closeChartFullscreen() {
	      if (!this.isChartFullscreen) return
	      this.isChartFullscreen = false
	      document.body.style.overflow = this.bodyOverflowBeforeFullscreen || ''
	      window.removeEventListener('resize', this.updateFullscreenChartHeight)
	      window.removeEventListener('orientationchange', this.updateFullscreenChartHeight)
	      document.removeEventListener('keydown', this.onFullscreenKeydown)
	    },
	    onFullscreenKeydown(event: KeyboardEvent) {
	      if (event.key === 'Escape') {
	        this.closeChartFullscreen()
	      }
	    },
	    updateFullscreenChartHeight() {
	      const padding = 140
	      this.fullscreenChartHeight = Math.max(320, window.innerHeight - padding)
	    },
	    updateChartHeight() {
	      const width = window.innerWidth
	      if (width <= 480) {
	        this.chartHeight = 420
	        return
	      }
	      if (width <= 768) {
	        this.chartHeight = 420
	        return
      }
      if (width <= 1024) {
        this.chartHeight = 520
        return
      }
      if (width <= 1600) {
        this.chartHeight = 650
        return
      }
      this.chartHeight = 720
    },
    resetZoom(chartId: string) {
      resetZoom(chartId)
    },
    exportToPng() {
      exportToImg('image/png', 'asset-chart', this.getExportSerialName() as any, this.isDarkMode)
    },
    exportToJpg() {
      exportToImg('image/jpeg', 'asset-chart', this.getExportSerialName() as any, this.isDarkMode)
    },
    exportCsv() {
      if (!this.chartHasData) return

      const exportRows: any[] = []
      if (this.isDevice) {
        const deviceSeries = [
          { key: 'x_axis', paramName: this.chartSeries.xAxis.paramName, label: this.chartSeries.xAxis.label, points: this.chartSeries.xAxis.points, unit: this.chartSeries.xAxis.unit },
          { key: 'y_axis', paramName: this.chartSeries.yAxis.paramName, label: this.chartSeries.yAxis.label, points: this.chartSeries.yAxis.points, unit: this.chartSeries.yAxis.unit },
          { key: 'z_axis', paramName: this.chartSeries.zAxis.paramName, label: this.chartSeries.zAxis.label, points: this.chartSeries.zAxis.points, unit: this.chartSeries.zAxis.unit },
          { key: 'magnitude', paramName: this.chartSeries.magnitude.paramName, label: this.chartSeries.magnitude.label, points: this.chartSeries.magnitude.points, unit: this.chartSeries.magnitude.unit },
          { key: 'magnitude_no_gravity', paramName: this.chartSeries.magnitudeNoGravity.paramName, label: this.chartSeries.magnitudeNoGravity.label, points: this.chartSeries.magnitudeNoGravity.points, unit: this.chartSeries.magnitudeNoGravity.unit }
        ]

        deviceSeries.forEach((series) => {
          if (!series.points.length) return
          series.points.forEach((point) => {
            exportRows.push({
              device_timestamp: Math.floor(point.x / 1000),
              series: series.key,
              param_name: series.paramName,
              label: series.label,
              value: point.y,
              unit: series.unit
            })
          })
        })
      } else if (this.isDknLmsAsset) {
        DKN_LMS_SERIES_KEYS.forEach((key) => {
          const series = this.chartSeries[key]
          if (!series || !series.points.length) return
          series.points.forEach((point) => {
            exportRows.push({
              device_timestamp: Math.floor(point.x / 1000),
              series: key,
              param_name: series.paramName,
              label: series.label,
              value: point.y,
              unit: series.unit
            })
          })
        })
      } else {
        Object.entries(this.chartSeries).forEach(([key, series]) => {
          if (!series.points.length) return
          series.points.forEach((point) => {
            exportRows.push({
              device_timestamp: Math.floor(point.x / 1000),
              series: key,
              param_name: series.paramName,
              label: series.label,
              value: point.y,
              unit: series.unit
            })
          })
        })
      }

      if (!exportRows.length) return

      const filename = `asset_history_${this.getExportSerialName()}_${getHumanDate(Date.now() / 1000)}`
      const locale = this.$i18n.locale
      generateCsv(exportRows, filename, this.$t, locale)
    },
    async fetchChartSeries(assetId: string | number, source?: 'asset' | 'device') {
      this.chartLoading = true
      this.chartError = null
      this.chartSeries = buildSeriesRecord({}, (key) => key)
      this.heatmapSeries = buildSeriesRecord({}, (key) => key)
      this.heatmapRangeStartTs = null
      this.heatmapRangeEndTs = null

      try {
        const now = new Date()
        now.setSeconds(0, 0)
        const dateTo = now.toISOString()
        const dateFrom = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString()
        const rangeStartMs = new Date(dateFrom).getTime()
        const rangeEndMs = new Date(dateTo).getTime()
        const rangeStartHourMs = Math.floor(rangeStartMs / (60 * 60 * 1000)) * (60 * 60 * 1000)
        const heatmapDateFrom = new Date(rangeStartHourMs - 60 * 60 * 1000).toISOString()

        const response = await getAssetHistoryRange(assetId, heatmapDateFrom, dateTo, source)
        const fetchedSeries = buildSeriesRecord(
          response.data?.series,
          (key) => this.$t(key) as string
        )
        this.heatmapSeries = fetchedSeries
        this.chartSeries = clipSeriesRecordByTimeRange(fetchedSeries, rangeStartMs, rangeEndMs)
        this.heatmapRangeStartTs = rangeStartMs
        this.heatmapRangeEndTs = rangeEndMs
      } catch (err) {
        console.error('Error fetching asset chart history:', err)
        this.chartError = this.$t('components.assetDetails.errorLoading') as string
      } finally {
        this.chartLoading = false
      }
    },
    async fetchEnergyStats(assetId: string | number) {
      this.energyStatsLoading = true
      try {
        const response = await getAssetEnergyStats(assetId)
        this.energyStats = response.data
      } catch (err) {
        console.error('Error fetching energy stats:', err)
      } finally {
        this.energyStatsLoading = false
      }
    },
    async fetchAlarms(assetId: string | number) {
      this.alarmsLoading = true
      this.alarmsError = null
      try {
        const response = await getAssetAlarms(assetId)
        this.alarms = response.data.alarms ?? []
        this.alarmsSummary = response.data.summary ?? null
      } catch (err) {
        console.error('Error fetching alarms:', err)
        this.alarmsError = this.$t('components.assetDetails.alarms.errorLoading') as string
      } finally {
        this.alarmsLoading = false
      }
    },
    wagoGroupHasData(group: WagoChartGroup): boolean {
      return group.keys.some((key) => (this.chartSeries[key]?.points?.length ?? 0) > 0)
    },
    wagoChartData(group: WagoChartGroup): ChartData<'line'> {
      const multiplier = group.yMultiplier ?? 1
      return {
        datasets: group.keys
          .filter((key) => (this.chartSeries[key]?.points?.length ?? 0) > 0)
          .map((key) => {
            const meta = SERIES_META[key]
            const series = this.chartSeries[key]
            const data = multiplier !== 1
              ? series.points.map((p) => ({ x: p.x, y: p.y * multiplier }))
              : series.points
            return {
              data,
              parsing: false,
              normalized: true,
              yAxisID: group.axisPerKey?.[key]?.axisId ?? 'y',
              label: series.label || meta.labelKey,
              borderColor: meta.color,
              backgroundColor: meta.color,
              borderWidth: 2,
              tension: 0.1
            } as any
          })
      }
    },
    wagoChartOptions(group: WagoChartGroup): ChartOptions<'line'> {
      const isPowerFactor = group.id === 'power-factor'
      const isCurrent = group.id === 'current'
      const isDark = this.isDarkMode

      const buildYScale = (unit: string, position: 'left' | 'right', minY?: number, maxY?: number, stepSize?: number) => ({
        type: 'linear',
        position,
        min: minY,
        max: maxY,
        title: { display: false },
        ticks: {
          color: isDark ? 'rgba(255, 255, 255, 0.8)' : undefined,
          callback: (value: any) => {
            const numeric = typeof value === 'number' ? value : parseFloat(String(value))
            if (!Number.isFinite(numeric)) return value
            return unit ? `${numeric} ${unit}` : String(numeric)
          },
          stepSize: stepSize ?? (isPowerFactor ? 0.1 : (isCurrent ? 0.05 : (unit === 'V' ? 5 : undefined)))
        },
        grid: { color: isDark ? 'rgba(255, 255, 255, 0.1)' : undefined }
      })

      const yScales: Record<string, any> = {}
      if (group.axisPerKey) {
        const seenAxes = new Set<string>()
        group.keys.forEach((key) => {
          const axisConfig = group.axisPerKey![key]
          if (!axisConfig || seenAxes.has(axisConfig.axisId)) return
          seenAxes.add(axisConfig.axisId)
          yScales[axisConfig.axisId] = buildYScale(axisConfig.unit, axisConfig.position, undefined, undefined, axisConfig.stepSize)
        })
      } else {
        yScales['y'] = buildYScale(group.unit, 'left', group.minY, group.maxY)
      }

      return {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        scales: {
          x: {
            type: 'time',
            time: {
              tooltipFormat: 'DD/MM/YYYY HH:mm',
              displayFormats: {
                millisecond: 'HH:mm:ss',
                second: 'HH:mm:ss',
                minute: 'HH:mm',
                hour: 'DD/MM HH:mm',
                day: 'DD/MM',
                week: 'DD/MM',
                month: 'MM/YYYY',
                quarter: 'MM/YYYY',
                year: 'YYYY'
              }
            },
            grid: { color: isDark ? 'rgba(255, 255, 255, 0.1)' : undefined },
            ticks: { color: isDark ? 'rgba(255, 255, 255, 0.8)' : undefined }
          },
          ...yScales
        },
        plugins: {
          decimation: {
            enabled: true,
            algorithm: 'lttb',
            samples: 500
          },
          legend: {
            position: 'bottom',
            labels: {
              color: isDark ? 'rgba(255, 255, 255, 0.9)' : undefined,
              font: { size: 13, weight: 600 },
              padding: 12,
              usePointStyle: true
            }
          },
          zoom: {
            pan: { enabled: true, mode: 'x' },
            zoom: { wheel: { enabled: true }, pinch: { enabled: true }, mode: 'x' }
          },
          tooltip: {
            callbacks: {
              label: (context: any) => {
                let unit: string
                if (group.axisPerKey) {
                  const axisId = context.dataset.yAxisID
                  const axisConfig = Object.values(group.axisPerKey).find((c) => c?.axisId === axisId)
                  unit = axisConfig?.unit || ''
                } else {
                  unit = group.unit || ''
                }
                const value = context.parsed.y
                const label = context.dataset.label || ''
                return unit ? `${label}: ${value} ${unit}` : `${label}: ${value}`
              }
            }
          }
        },
        showLine: true,
        interaction: { mode: 'index', intersect: false },
        elements: { point: { radius: 0, hitRadius: 5 } },
        layout: { padding: { left: 20, right: 20, top: 20, bottom: 10 } }
      }
    },
    exportWagoGroupToPng(group: WagoChartGroup) {
      exportToImg('image/png', `wago-chart-${group.id}`, this.getExportSerialName(), this.isDarkMode)
    },
    exportWagoGroupToJpg(group: WagoChartGroup) {
      exportToImg('image/jpeg', `wago-chart-${group.id}`, this.getExportSerialName(), this.isDarkMode)
    },
    exportWagoGroupCsv(group: WagoChartGroup) {
      const exportRows: any[] = []
      group.keys.forEach((key) => {
        const series = this.chartSeries[key]
        if (!series?.points?.length) return
        series.points.forEach((point) => {
          exportRows.push({
            device_timestamp: Math.floor(point.x / 1000),
            series: key,
            param_name: series.paramName,
            label: series.label,
            value: point.y,
            unit: series.unit
          })
        })
      })
      if (!exportRows.length) return
      const filename = `wago_${group.id}_${this.getExportSerialName()}_${getHumanDate(Date.now() / 1000)}`
      generateCsv(exportRows, filename, this.$t, this.$i18n.locale)
    },
    dknGroupHasData(group: DknChartGroup): boolean {
      return group.keys.some((key) => (this.chartSeries[key]?.points?.length ?? 0) > 0)
    },
    dknGroupActiveKeys(group: DknChartGroup): SeriesKey[] {
      return group.keys.filter((key) => (this.chartSeries[key]?.points?.length ?? 0) > 0)
    },
    dknStatusTimelineLaneLabel(key: SeriesKey, fallback: string): string {
      const shortLabelKeys: Partial<Record<SeriesKey, string>> = {
        dknLmsDfs: 'components.assetDetails.statusTimelineShortLabels.defrostStatus',
        dknLmsDfm: 'components.assetDetails.statusTimelineShortLabels.defrostCommand',
        dknLmsD2: '',
        dknLmsDas: 'components.assetDetails.statusTimelineShortLabels.dayEcoMode',
        dknLmsCht: 'components.assetDetails.statusTimelineShortLabels.dirtyCondenser',
        dknLmsFia: 'components.assetDetails.statusTimelineShortLabels.externalAlarm',
        dknLmsHi: 'components.assetDetails.statusTimelineShortLabels.highTempAlarm',
        dknLmsGlo: 'components.assetDetails.statusTimelineShortLabels.genericAlarm',
        dknLmsU1: 'components.assetDetails.statusTimelineShortLabels.communicationErrorU1',
        dknLmsFoi: 'components.assetDetails.statusTimelineShortLabels.evaporatorFan',
        dknLmsFot: 'components.assetDetails.statusTimelineShortLabels.condenserFan'
      }
      const translationKey = shortLabelKeys[key]
      if (!translationKey) return fallback
      return this.$t(translationKey) as string
    },
    dknChartData(group: DknChartGroup): ChartData<'line'> {
      const activeKeys = this.dknGroupActiveKeys(group)
      const groupMode = group.mode ?? 'line'

      if (groupMode === 'statusTimeline') {
        return {
          datasets: activeKeys.map((key, index) => {
            const meta = SERIES_META[key]
            const series = this.chartSeries[key]
            const laneOffset = index * 2
            return {
              data: (series?.points || []).map((p) => ({
                x: p.x,
                y: (Math.round(Number(p.y)) >= 1 ? 1 : 0) + laneOffset,
                rawY: p.y
              })),
              parsing: false,
              normalized: true,
              yAxisID: 'dknStatusTimeline',
              label: series?.label || (this.$t(meta.labelKey) as string),
              borderColor: meta.color,
              backgroundColor: meta.color,
              borderWidth: 2,
              stepped: true,
              tension: 0,
              pointRadius: 0,
              pointHitRadius: 5,
              customKey: key,
              hidden: this.dknSeriesVisibility[key] === false
            } as any
          })
        }
      }

      if (groupMode === 'virtualProbe') {
        const key = activeKeys[0]
        if (!key) {
          return { datasets: [] }
        }
        const meta = SERIES_META[key]
        const series = this.chartSeries[key]
        return {
          datasets: [
            {
              data: series?.points || [],
              parsing: false,
              normalized: true,
              yAxisID: meta.axisId,
              label: series?.label || (this.$t(meta.labelKey) as string),
              borderColor: meta.color,
              backgroundColor: meta.color,
              borderWidth: 2,
              tension: 0.1
            } as any
          ]
        }
      }

      return {
        datasets: activeKeys
          .map((key) => {
            const meta = SERIES_META[key]
            const series = this.chartSeries[key]
            const isStep = groupMode === 'step'
            const isSetpoints = group.id === 'setpoints'
            const dashed = isSetpoints && key !== 'dknLmsSt'
            return {
              data: series?.points || [],
              parsing: false,
              normalized: true,
              yAxisID: meta.axisId,
              label: series?.label || (this.$t(meta.labelKey) as string),
              borderColor: meta.color,
              backgroundColor: meta.color,
              borderWidth: 2,
              tension: isStep ? 0 : 0.1,
              stepped: isStep,
              borderDash: dashed ? [7, 5] : undefined,
              customKey: key,
              hidden: this.dknSeriesVisibility[key] === false
            } as any
          })
      }
    },
    dknChartOptions(group: DknChartGroup): ChartOptions<'line'> {
      const isDark = this.isDarkMode
      const activeKeys = this.dknGroupActiveKeys(group)
      const groupMode = group.mode ?? 'line'
      const isStatusTimelineGroup = groupMode === 'statusTimeline'
      const isFanOutputsGroup = group.id === 'fan-outputs'
      const isPercentGroup = group.keys.length > 0 && group.keys.every((key) => SERIES_META[key].unit === '%')
      const fanEnabledLabel = this.$t('components.assetDetails.dknTooltipStates.enabled') as string
      const fanDisabledLabel = this.$t('components.assetDetails.dknTooltipStates.disabled') as string

      const scales: any = {
        x: {
          type: 'time',
          time: {
            tooltipFormat: 'DD/MM/YYYY HH:mm',
            displayFormats: {
              millisecond: 'HH:mm:ss',
              second: 'HH:mm:ss',
              minute: 'HH:mm',
              hour: 'DD/MM HH:mm',
              day: 'DD/MM'
            }
          },
          grid: {
            color: isDark ? 'rgba(255, 255, 255, 0.1)' : undefined
          },
          ticks: {
            color: isDark ? 'rgba(255, 255, 255, 0.8)' : undefined
          }
        }
      }

      const axisMeta: Record<string, { title: string; showTitle: boolean; position: 'left' | 'right'; color: string; offset?: boolean; unit: string; stepSize?: number }> = {}
      const laneLabels = activeKeys.map((key) => {
        const fullLabel = this.chartSeries[key]?.label || (this.$t(SERIES_META[key].labelKey) as string)
        return this.dknStatusTimelineLaneLabel(key, fullLabel)
      })

      if (isStatusTimelineGroup) {
        const maxLane = Math.max(1, activeKeys.length * 2)
        const capturedVisibility = this.dknSeriesVisibility
        scales.dknStatusTimeline = {
          type: 'linear',
          position: 'left',
          min: -0.5,
          max: maxLane - 0.5,
          title: {
            display: false
          },
          ticks: {
            color: isDark ? 'rgba(255, 255, 255, 0.8)' : '#64748b',
            autoSkip: false,
            stepSize: 1,
            font: { size: 10 },
            callback: function (value: any) {
              const numeric = typeof value === 'number' ? value : parseFloat(String(value))
              if (!Number.isFinite(numeric)) return ''
              for (let idx = 0; idx < laneLabels.length; idx++) {
                const laneCenter = idx * 2 + 1
                if (Math.abs(numeric - laneCenter) < 0.2) {
                  if (capturedVisibility[activeKeys[idx]] === false) return ''
                  return laneLabels[idx] || ''
                }
              }
              return ''
            }
          },
          grid: {
            color: isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(15, 23, 42, 0.06)'
          }
        }
      } else {
        activeKeys.forEach((key) => {
          const meta = SERIES_META[key]
          if (!axisMeta[meta.axisId]) {
            const showTitle = !!meta.axisTitle
            const title = meta.axisTitle && meta.axisTitle.startsWith('components.')
              ? (this.$t(meta.axisTitle) as string)
              : (meta.axisTitle || '')
            axisMeta[meta.axisId] = {
              title,
              showTitle,
              position: meta.axisPosition,
              color: meta.color,
              offset: meta.offset,
              unit: meta.unit,
              stepSize: meta.stepSize
            }
          }
        })

        Object.entries(axisMeta).forEach(([axisId, meta]) => {
          const isHz = meta.unit === 'Hz'
          const isTemperature = meta.unit === '°C'
          const isKelvin = meta.unit === 'K'
          const isAlarmCountGroup = group.id === 'alarm-count'
          const isIntegerCountAxis = axisId === 'dknUartCount'

          scales[axisId] = {
            type: 'linear',
            position: meta.position,
            offset: meta.offset,
            min: isPercentGroup ? 0 : (isIntegerCountAxis ? 0 : undefined),
            max: isPercentGroup ? 100 : undefined,
            grace: meta.stepSize ? '5%' : undefined,
            title: {
              display: isIntegerCountAxis ? false : meta.showTitle,
              text: meta.title,
              color: meta.color,
              font: {
                weight: 'bold'
              }
            },
            ticks: {
              color: meta.color,
              font: {
                weight: 'bold'
              },
              stepSize: meta.stepSize ?? (isAlarmCountGroup || isIntegerCountAxis ? 1 : (isPercentGroup ? 5 : (isHz ? 5 : (isTemperature || isKelvin ? 5 : undefined)))),
              callback: function (value: any) {
                const numeric = typeof value === 'number' ? value : parseFloat(String(value))
                if (!Number.isFinite(numeric)) return value
                return meta.unit ? `${numeric} ${meta.unit}` : `${numeric}`
              }
            },
            grid: {
              drawOnChartArea: meta.position === 'right',
              color: isDark ? 'rgba(255, 255, 255, 0.06)' : undefined
            }
          }
        })
      }

      return {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        scales,
        plugins: {
          decimation: {
            enabled: !isStatusTimelineGroup,
            algorithm: 'lttb',
            samples: 500
          },
          legend: {
            position: 'bottom',
            labels: {
              color: isDark ? 'rgba(255, 255, 255, 0.9)' : undefined,
              font: {
                size: 13,
                weight: 600
              },
              padding: 12,
              usePointStyle: true
            },
            onClick: (_event: any, legendItem: any, legend: any) => {
              const datasetIndex = legendItem.datasetIndex
              if (typeof datasetIndex !== 'number') return
              const dataset = legend.chart.data.datasets[datasetIndex] as any
              const key = dataset?.customKey as string | undefined
              if (!key) return
              const currentlyVisible = this.dknSeriesVisibility[key] !== false
              this.dknSeriesVisibility = { ...this.dknSeriesVisibility, [key]: !currentlyVisible }
            }
          },
          zoom: {
            pan: {
              enabled: true,
              mode: 'x'
            },
            zoom: {
              wheel: {
                enabled: true
              },
              pinch: {
                enabled: true
              },
              mode: 'x'
            }
          },
          tooltip: {
            callbacks: {
              label: function (context: any) {
                const label = context.dataset.label || ''
                const rawPoint = context.raw || {}
                if (isStatusTimelineGroup) {
                  const rawValue = typeof rawPoint?.rawY === 'number'
                    ? rawPoint.rawY
                    : (Math.round(Number(context.parsed.y)) % 2)
                  const isActive = Math.round(Number(rawValue)) >= 1
                  if (isFanOutputsGroup) {
                    return `${label}: ${isActive ? fanEnabledLabel : fanDisabledLabel}`
                  }
                  return `${label}: ${isActive ? '1' : '0'}`
                }
                const value = typeof rawPoint?.rawY === 'number' ? rawPoint.rawY : context.parsed.y
                const axisId = context.dataset.yAxisID
                const unit = axisMeta[axisId]?.unit || ''
                if (context.dataset?._dknOutlier) {
                  return unit ? `${label}: ${value} ${unit} (outlier)` : `${label}: ${value} (outlier)`
                }
                return unit ? `${label}: ${value} ${unit}` : `${label}: ${value}`
              }
            }
          }
        },
        showLine: true,
        interaction: {
          mode: isStatusTimelineGroup ? 'nearest' : 'index',
          intersect: false
        },
        elements: {
          point: {
            radius: 0,
            hitRadius: 5
          }
        },
        layout: {
          padding: {
            left: 20,
            right: 20,
            top: 20,
            bottom: 10
          }
        }
      }
    },
    exportDknGroupToPng(group: DknChartGroup) {
      exportToImg('image/png', `dkn-chart-${group.id}`, this.getExportSerialName(), this.isDarkMode)
    },
    exportDknGroupToJpg(group: DknChartGroup) {
      exportToImg('image/jpeg', `dkn-chart-${group.id}`, this.getExportSerialName(), this.isDarkMode)
    },
    exportDknGroupCsv(group: DknChartGroup) {
      const exportRows: any[] = []
      group.keys.forEach((key) => {
        const series = this.chartSeries[key]
        if (!series?.points?.length) return
        series.points.forEach((point) => {
          exportRows.push({
            device_timestamp: Math.floor(point.x / 1000),
            series: key,
            param_name: series.paramName,
            label: series.label,
            value: point.y,
            unit: series.unit
          })
        })
      })
      if (!exportRows.length) return
      const filename = `dkn_${group.id}_${this.getExportSerialName()}_${getHumanDate(Date.now() / 1000)}`
      generateCsv(exportRows, filename, this.$t, this.$i18n.locale)
    },
    exportMpsGroupToPng(group: DknChartGroup) {
      exportToImg('image/png', `mps-chart-${group.id}`, this.getExportSerialName(), this.isDarkMode)
    },
    exportMpsGroupToJpg(group: DknChartGroup) {
      exportToImg('image/jpeg', `mps-chart-${group.id}`, this.getExportSerialName(), this.isDarkMode)
    },
    exportMpsGroupCsv(group: DknChartGroup) {
      this.exportChartGroupCsv(group, 'mps')
    },
    exportUartGroupToPng(group: DknChartGroup) {
      exportToImg('image/png', `uart-chart-${group.id}`, this.getExportSerialName(), this.isDarkMode)
    },
    exportUartGroupToJpg(group: DknChartGroup) {
      exportToImg('image/jpeg', `uart-chart-${group.id}`, this.getExportSerialName(), this.isDarkMode)
    },
    exportUartGroupCsv(group: DknChartGroup) {
      this.exportChartGroupCsv(group, 'uart')
    },
    exportChartGroupCsv(group: DknChartGroup, filenamePrefix: string) {
      const exportRows: any[] = []
      group.keys.forEach((key) => {
        const series = this.chartSeries[key]
        if (!series?.points?.length) return
        series.points.forEach((point) => {
          exportRows.push({
            device_timestamp: Math.floor(point.x / 1000),
            series: key,
            param_name: series.paramName,
            label: series.label,
            value: point.y,
            unit: series.unit
          })
        })
      })
      if (!exportRows.length) return
      const filename = `${filenamePrefix}_${group.id}_${this.getExportSerialName()}_${getHumanDate(Date.now() / 1000)}`
      generateCsv(exportRows, filename, this.$t, this.$i18n.locale)
    }
	  },
	  computed: {
    isModbusAsset(): boolean {
      const flag = this.asset?.is_modbus ?? this.asset?.isModbus
      return !!flag
    },
    isWagoModbusAsset(): boolean {
      if (!this.isModbusAsset || this.isDknLmsAsset) {
        return false
      }

      const params = this.asset?.latest_params
      if (!Array.isArray(params) || !params.length) {
        return false
      }

      return params.some((param: any) => {
        const name = param?.can_params_io?.param_name
        return typeof name === 'string' && name.startsWith('WAGO_')
      })
    },
    isDknUartAsset(): boolean {
      const modelId = Number(this.asset?.device_model_id ?? this.asset?.device_model?.id)
      if (modelId === 31) return true
      const params = this.asset?.latest_params
      if (Array.isArray(params) && params.length) {
        return params.some((param: any) => {
          const name = param?.can_params_io?.param_name
          return typeof name === 'string' && name.startsWith('DKN_UART_')
        })
      }
      return false
    },
    isDknMpsAsset(): boolean {
      const modelId = Number(this.asset?.device_model_id ?? this.asset?.device_model?.id)
      if (modelId === 25 || modelId === 30) return true
      const params = this.asset?.latest_params
      if (Array.isArray(params) && params.length) {
        return params.some((param: any) => {
          const name = param?.can_params_io?.param_name
          return typeof name === 'string' && name.startsWith('DKN_MPS_')
        })
      }
      return false
    },
    isDknLmsAsset(): boolean {
      if (this.isDknUartAsset) return false
      if (this.isDknMpsAsset) return false
      if (this.asset?.device_model?.brand?.name === 'Daikin') {
        return true
      }
      const params = this.asset?.latest_params
      if (Array.isArray(params) && params.length) {
        if (params.some((param: any) => {
          const name = param?.can_params_io?.param_name
          return typeof name === 'string' && name.startsWith('DKN_LMS_')
        })) {
          return true
        }
      }
      return DKN_LMS_SERIES_KEYS.some((key) => (this.chartSeries[key]?.points?.length ?? 0) > 0)
    },
    wagoChartGroups(): WagoChartGroup[] {
      return WAGO_CHART_GROUPS
    },
    wagoHasAnyData(): boolean {
      return WAGO_CHART_GROUPS.some((group) => this.wagoGroupHasData(group))
    },
    dknChartGroups(): DknChartGroup[] {
      return DKN_LMS_CHART_GROUPS
    },
    dknHasAnyData(): boolean {
      return DKN_LMS_CHART_GROUPS.some((group) => this.dknGroupHasData(group))
    },
    mpsChartGroups(): DknChartGroup[] {
      return DKN_MPS_CHART_GROUPS
    },
    mpsHasAnyData(): boolean {
      return DKN_MPS_CHART_GROUPS.some((group) => this.dknGroupHasData(group))
    },
    uartChartGroups(): DknChartGroup[] {
      return DKN_UART_CHART_GROUPS
    },
    uartHasAnyData(): boolean {
      return DKN_UART_CHART_GROUPS.some((group) => this.dknGroupHasData(group))
    },
	    chartRenderKey(): string {
	      return `${this.isDarkMode ? 'dark' : 'light'}-${this.isChartFullscreen ? 'fullscreen' : 'inline'}`
	    },
    chartHasData(): boolean {
        if (this.isDevice) {
          return !!(
            this.chartSeries.xAxis.points.length ||
            this.chartSeries.yAxis.points.length ||
            this.chartSeries.zAxis.points.length ||
            this.chartSeries.magnitude.points.length ||
            this.chartSeries.magnitudeNoGravity.points.length
          )
        }
        if (this.isDknLmsAsset) {
          return DKN_LMS_SERIES_KEYS.some((key) => this.chartSeries[key]?.points?.length)
        }
        if (this.isDknMpsAsset) {
          return DKN_MPS_SERIES_KEYS.some((key) => this.chartSeries[key]?.points?.length)
        }
	      return !!(this.chartSeries.temperature.points.length || this.chartSeries.humidity.points.length)
	    },
	    chartData(): ChartData<'line'> {
      if (this.isDevice) {
        const xMeta = SERIES_META.xAxis
        const yMeta = SERIES_META.yAxis
        const zMeta = SERIES_META.zAxis
        const magnitudeMeta = SERIES_META.magnitude
        const magnitudeNoGravityMeta = SERIES_META.magnitudeNoGravity
        return {
          datasets: [
            {
              data: this.chartSeries.xAxis.points,
              yAxisID: 'axis',
              label: this.$t(xMeta.labelKey) as string,
              borderColor: xMeta.color,
              backgroundColor: xMeta.color,
              borderWidth: 2,
              tension: 0.1
            },
            {
              data: this.chartSeries.yAxis.points,
              yAxisID: 'axis',
              label: this.$t(yMeta.labelKey) as string,
              borderColor: yMeta.color,
              backgroundColor: yMeta.color,
              borderWidth: 2,
              tension: 0.1
            },
            {
              data: this.chartSeries.zAxis.points,
              yAxisID: 'axis',
              label: this.$t(zMeta.labelKey) as string,
              borderColor: zMeta.color,
              backgroundColor: zMeta.color,
              borderWidth: 2,
              tension: 0.1
            },
            {
              data: this.chartSeries.magnitude.points,
              yAxisID: 'axis',
              label: this.$t(magnitudeMeta.labelKey) as string,
              borderColor: magnitudeMeta.color,
              backgroundColor: magnitudeMeta.color,
              borderWidth: 2,
              tension: 0.1
            },
            {
              data: this.chartSeries.magnitudeNoGravity.points,
              yAxisID: 'axis',
              label: this.$t(magnitudeNoGravityMeta.labelKey) as string,
              borderColor: magnitudeNoGravityMeta.color,
              backgroundColor: magnitudeNoGravityMeta.color,
              borderWidth: 2,
              borderDash: [6, 4],
              tension: 0.1
            }
          ]
        }
      }

      if (this.isDknLmsAsset) {
        return {
          datasets: DKN_LMS_SERIES_KEYS
            .filter((key) => this.chartSeries[key]?.points?.length)
            .map((key) => {
              const meta = SERIES_META[key]
              const series = this.chartSeries[key]
              return {
                data: series?.points || [],
                yAxisID: meta.axisId,
                label: series?.label || (this.$t(meta.labelKey) as string),
                borderColor: meta.color,
                backgroundColor: meta.color,
                borderWidth: 2,
                tension: 0.1
              }
            })
        }
      }

      const temperatureSeries = this.chartSeries.temperature.points
      const humiditySeries = this.chartSeries.humidity.points

      return {
        datasets: [
          {
            data: temperatureSeries,
            yAxisID: 'temperature',
            label: this.$t(SERIES_META.temperature.labelKey),
            borderColor: SERIES_META.temperature.color,
            backgroundColor: SERIES_META.temperature.color,
            borderWidth: 2,
            tension: 0.1
          },
          {
            data: humiditySeries,
            yAxisID: 'humidity',
            label: this.$t(SERIES_META.humidity.labelKey),
            borderColor: SERIES_META.humidity.color,
            backgroundColor: SERIES_META.humidity.color,
            borderWidth: 2,
            tension: 0.1
          }
        ]
      }
    },
    chartOptions(): ChartOptions<'line'> {
      if (this.isDevice) {
        const allValues = [
          ...this.chartSeries.xAxis.points,
          ...this.chartSeries.yAxis.points,
          ...this.chartSeries.zAxis.points,
          ...this.chartSeries.magnitude.points,
          ...this.chartSeries.magnitudeNoGravity.points
        ].map((point) => point.y)
        const min = allValues.length ? Math.min(...allValues) : null
        const max = allValues.length ? Math.max(...allValues) : null
        const range = min !== null && max !== null ? Math.abs(max - min) : null
        const padding = range !== null ? Math.max(1, range * 0.25) : null

        return {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              type: 'time',
              time: {
                tooltipFormat: 'DD/MM/YYYY HH:mm',
                displayFormats: {
                  'millisecond': 'HH:mm:ss',
                  'second': 'HH:mm:ss',
                  'minute': 'HH:mm',
                  'hour': 'DD/MM HH:mm',
                  'day': 'DD/MM'
                }
              },
              grid: {
                color: this.isDarkMode ? 'rgba(255, 255, 255, 0.1)' : undefined
              },
              ticks: {
                color: this.isDarkMode ? 'rgba(255, 255, 255, 0.8)' : undefined
              }
            },
            axis: {
              type: 'linear',
              position: 'left',
              suggestedMin: min !== null && padding !== null ? min - padding : undefined,
              suggestedMax: max !== null && padding !== null ? max + padding : undefined,
              title: {
                display: true,
                text: this.$t('components.assetDetails.axisTitleDevice') as string,
                color: this.isDarkMode ? 'rgba(255, 255, 255, 0.8)' : undefined,
                font: {
                  weight: 'bold'
                }
              },
              ticks: {
                color: this.isDarkMode ? 'rgba(255, 255, 255, 0.8)' : undefined
              },
              grid: {
                color: this.isDarkMode ? 'rgba(255, 255, 255, 0.1)' : undefined
              }
            }
          },
          plugins: {
            legend: {
              position: 'bottom',
              labels: {
                color: this.isDarkMode ? 'rgba(255, 255, 255, 0.9)' : undefined,
                font: {
                  size: 13,
                  weight: 600
                },
                padding: 12,
                usePointStyle: true
              }
            },
            title: {
              display: false,
              text: this.$t('components.assetDetails.chartTitle'),
              color: this.isDarkMode ? '#ffffff' : '#000',
              font: {
                weight: 'bold',
                size: 24
              },
              padding: this.isDarkMode ? {
                top: 10,
                bottom: 20
              } : undefined
            },
            zoom: {
              pan: {
                enabled: true,
                mode: 'x'
              },
              zoom: {
                wheel: {
                  enabled: true
                },
                pinch: {
                  enabled: true
                },
                mode: 'x'
              }
            }
          },
          showLine: true,
          interaction: {
            mode: 'index',
            intersect: false
          },
          elements: {
            point: {
              radius: 0,
              hitRadius: 5
            }
          },
          layout: {
            padding: {
              left: 20,
              right: 20,
              top: 20,
              bottom: 10
            }
          }
        }
      }

      if (this.isDknLmsAsset) {
        const scales: any = {
          x: {
            type: 'time',
            time: {
              tooltipFormat: 'DD/MM/YYYY HH:mm',
              displayFormats: {
                'millisecond': 'HH:mm:ss',
                'second': 'HH:mm:ss',
                'minute': 'HH:mm',
                'hour': 'DD/MM HH:mm',
                'day': 'DD/MM'
              }
            },
            grid: {
              color: this.isDarkMode ? 'rgba(255, 255, 255, 0.1)' : undefined
            },
            ticks: {
              color: this.isDarkMode ? 'rgba(255, 255, 255, 0.8)' : undefined
            }
          }
        }

        const axisMeta: Record<string, { title: string; showTitle: boolean; position: 'left' | 'right'; color: string; offset?: boolean; unit: string }> = {}
        DKN_LMS_SERIES_KEYS.forEach((key) => {
          const meta = SERIES_META[key]
          if (!axisMeta[meta.axisId]) {
            const showTitle = !!meta.axisTitle
            const title = meta.axisTitle && meta.axisTitle.startsWith('components.')
              ? (this.$t(meta.axisTitle) as string)
              : (meta.axisTitle || '')
            axisMeta[meta.axisId] = {
              title,
              showTitle,
              position: meta.axisPosition,
              color: meta.color,
              offset: meta.offset,
              unit: meta.unit
            }
          }
        })

        Object.entries(axisMeta).forEach(([axisId, meta]) => {
          const isPercent = meta.unit === '%'
          const isHz = meta.unit === 'Hz'
          const isTemperature = meta.unit === '°C'
          const isKelvin = meta.unit === 'K'
          const isStatus = meta.unit === ''
          scales[axisId] = {
            type: 'linear',
            position: meta.position,
            offset: meta.offset,
            min: isStatus ? 0 : undefined,
            max: isStatus ? 1 : undefined,
            title: {
              display: meta.showTitle,
              text: meta.title,
              color: meta.color,
              font: {
                weight: 'bold'
              }
            },
            ticks: {
              color: meta.color,
              font: {
                weight: 'bold'
              },
              stepSize: isStatus ? 1 : (isPercent ? 5 : (isHz ? 5 : (isTemperature || isKelvin ? 5 : undefined))),
              callback: function (value: any) {
                const numeric = typeof value === 'number' ? value : parseFloat(String(value))
                if (!Number.isFinite(numeric)) return value
                if (isStatus) {
                  const rounded = Math.round(numeric)
                  return rounded >= 1 ? '1' : '0'
                }
                return meta.unit ? `${numeric} ${meta.unit}` : `${numeric}`
              }
            },
            grid: {
              drawOnChartArea: meta.position === 'right',
              color: this.isDarkMode ? 'rgba(255, 255, 255, 0.06)' : undefined
            }
          }
        })

        return {
          responsive: true,
          maintainAspectRatio: false,
          scales,
          plugins: {
            legend: {
              position: 'bottom',
              labels: {
                color: this.isDarkMode ? 'rgba(255, 255, 255, 0.9)' : undefined,
                font: {
                  size: 13,
                  weight: 600
                },
                padding: 12,
                usePointStyle: true
              }
            },
            title: {
              display: false,
              text: this.$t('components.assetDetails.chartTitle'),
              color: this.isDarkMode ? '#ffffff' : '#000',
              font: {
                weight: 'bold',
                size: 24
              },
              padding: this.isDarkMode ? {
                top: 10,
                bottom: 20
              } : undefined
            },
            zoom: {
              pan: {
                enabled: true,
                mode: 'x'
              },
              zoom: {
                wheel: {
                  enabled: true
                },
                pinch: {
                  enabled: true
                },
                mode: 'x'
              }
            },
            tooltip: {
              callbacks: {
                label: function (context: any) {
                  const label = context.dataset.label || ''
                  const value = context.parsed.y
                  const axisId = context.dataset.yAxisID
                  const unit = axisMeta[axisId]?.unit || ''
                  return unit ? `${label}: ${value} ${unit}` : `${label}: ${value}`
                }
              }
            }
          },
          showLine: true,
          interaction: {
            mode: 'index',
            intersect: false
          },
          elements: {
            point: {
              radius: 0,
              hitRadius: 5
            }
          },
          layout: {
            padding: {
              left: 20,
              right: 20,
              top: 20,
              bottom: 10
            }
          }
        }
      }

      const temperatureNumbers = this.chartSeries.temperature.points.map((point) => point.y)

      const tempMin = temperatureNumbers.length ? Math.min(...temperatureNumbers) : null
      const tempMax = temperatureNumbers.length ? Math.max(...temperatureNumbers) : null
      const tempRange = tempMin !== null && tempMax !== null ? Math.abs(tempMax - tempMin) : null
      const tempPadding = tempRange !== null ? Math.max(0.5, tempRange * 0.25) : null

      return {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            type: 'time',
            time: {
              tooltipFormat: 'DD/MM/YYYY HH:mm',
              displayFormats: {
                'millisecond': 'HH:mm:ss',
                'second': 'HH:mm:ss',
                'minute': 'HH:mm',
                'hour': 'DD/MM HH:mm',
                'day': 'DD/MM'
              }
            },
            grid: {
              color: this.isDarkMode ? 'rgba(255, 255, 255, 0.1)' : undefined
            },
            ticks: {
              color: this.isDarkMode ? 'rgba(255, 255, 255, 0.8)' : undefined
            }
          },
          temperature: {
            type: 'linear',
            position: 'left',
            suggestedMin: tempMin !== null && tempPadding !== null ? tempMin - tempPadding : undefined,
            suggestedMax: tempMax !== null && tempPadding !== null ? tempMax + tempPadding : undefined,
            title: {
              display: true,
              text: this.$t(SERIES_META.temperature.labelKey),
              color: SERIES_META.temperature.color,
              font: {
                weight: 'bold'
              }
            },
            ticks: {
              color: SERIES_META.temperature.color,
              font: {
                weight: 'bold'
              },
              callback: function (value) {
                const numeric = typeof value === 'number' ? value : parseFloat(String(value))
                return Number.isFinite(numeric) ? `${numeric.toFixed(1)} °C` : `${value} °C`
              }
            },
            grid: {
              color: this.isDarkMode ? 'rgba(246, 127, 1, 0.15)' : undefined
            }
          },
          humidity: {
            type: 'linear',
            position: 'right',
            min: 0,
            max: 100,
            title: {
              display: true,
              text: this.$t(SERIES_META.humidity.labelKey),
              color: SERIES_META.humidity.color,
              font: {
                weight: 'bold'
              }
            },
            ticks: {
              stepSize: 10,
              color: SERIES_META.humidity.color,
              font: {
                weight: 'bold'
              },
              callback: function (value) {
                return value + ' %'
              }
            },
            grid: {
              drawOnChartArea: false,
              color: this.isDarkMode ? 'rgba(59, 130, 246, 0.15)' : undefined
            }
          }
        },
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              color: this.isDarkMode ? 'rgba(255, 255, 255, 0.9)' : undefined,
              font: {
                size: 13,
                weight: 600
              },
              padding: 12,
              usePointStyle: true
            }
          },
          title: {
            display: false,
            text: this.$t('components.assetDetails.chartTitle'),
            color: this.isDarkMode ? '#ffffff' : '#000',
            font: {
              weight: 'bold',
              size: 24
            },
            padding: this.isDarkMode ? {
              top: 10,
              bottom: 20
            } : undefined
          },
          zoom: {
            pan: {
              enabled: true,
              mode: 'x'
            },
            zoom: {
              wheel: {
                enabled: true
              },
              pinch: {
                enabled: true
              },
              mode: 'x'
            }
          }
        },
        showLine: true,
        interaction: {
          mode: 'index',
          intersect: false
        },
        elements: {
          point: {
            radius: 0,
            hitRadius: 5
          }
        },
        layout: {
          padding: {
            left: 20,
            right: 20,
            top: 20,
            bottom: 10
          }
        }
      }
    },
    chartPlugins(): any[] {
      const bgColor = this.isDarkMode ? '#1e1e2d' : '#f5f8fa'
      return [
        {
          id: 'customCanvasBackgroundColor',
          beforeDraw: (chart: any) => {
            const ctx = chart.ctx
            ctx.save()
            ctx.fillStyle = bgColor
            ctx.fillRect(0, 0, chart.width, chart.height)
            ctx.restore()
          }
        }
      ]
    }
  }
})
</script>

<style scoped lang="scss">
@import '@/assets/scss/asset-detail-view-style.scss';
</style>
